import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  IDriverManifestsAlertReport,
  IDriverManifestsDriver,
  IDriverManifestsRetrieveParameters,
  IDriverManifestsTrip
} from 'src/types/driver-manifests'
import DriverManifestsDriverItem from './DriverManifestsDriverItem'
import DriverManifestsTripItem from './DriverManifestsTripItem'
import DriverManifestsTripDetailsPane from './DriverManifestsTripDetailsPane'
import { Input, Segmented, SegmentedGroup } from '@mobiscroll/react-latest'
import { tripService } from 'src/services/Trip'
import moment from 'moment'
import { debounce } from 'throttle-debounce'
import { useDriverManifestsContext } from './DriverManifestsContext'
import DriverManifestsNotificationPopup from './DriverManifestsNotificationPopup'
import { cancelledTripsStatusesDashboard, TripStatuses } from 'src/constants/trip-statuses'
import { MoonLoader } from 'react-spinners'

const INTERVAL = 30000

interface Notification {
  id: string
  title: string
  body: string
  isIssue: boolean
}

const DriverManifestsPage = () => {
  const { drivers, setDrivers, trips, setTrips, setIsUpdated, refresh, setRefresh } = useDriverManifestsContext()
  const [totalDrivers, setTotalDrivers] = useState<number>(0)
  const [selectedDriver, setSelectedDriver] = useState<IDriverManifestsDriver | null>(null)
  const [selectedTrip, setSelectedTrip] = useState<IDriverManifestsTrip | null>(null)
  const [query, setQuery] = useState<string>('')
  const [tab, setTab] = useState<'all' | 'alerts' | 'behind' | 'no_show' | 'vehicle'>('all')
  const [alertCount, setAlertCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [activeNotifications, setActiveNotifications] = useState<Notification[]>([])
  const [queuedNotifications, setQueuedNotifications] = useState<Notification[]>([])

  const tabRef = useRef(tab)
  const queryRef = useRef(query)
  const prevSelectedDriver = useRef<IDriverManifestsDriver | null>(null)
  const seenTripsRef = useRef<Map<string, string>>(new Map())
  const changeCount = useRef(0)

  useEffect(() => {
    tabRef.current = tab
  }, [tab])

  useEffect(() => {
    queryRef.current = query
  }, [query])

  useEffect(() => {
    getOperators()
  }, [])

  useEffect(() => {
    if (refresh) {
      getOperators()
      setQuery('')
      setTab('all')
      setSelectedDriver(null)
      setSelectedTrip(null)
      setRefresh(false)
    }
  }, [refresh])

  useEffect(() => {
    getTotalAlertCount()
    checkSelectedDriver()
  }, [drivers])

  useEffect(() => {
    checkSelectedTrip()
  }, [trips])

  useEffect(() => {
    const prev = prevSelectedDriver.current
    if (selectedDriver) {
      setTrips(selectedDriver.trips)
      if (prev && selectedTrip && prev.operatorId === selectedDriver.operatorId) {
        const updatedSelectedTrip = selectedDriver.trips.find(trip => selectedTrip.id === trip.id)
        if (updatedSelectedTrip) {
          setSelectedTrip(updatedSelectedTrip)
        }
      } else {
        setSelectedTrip(null)
      }
    } else {
      setSelectedTrip(null)
      setTrips([])
    }
    prevSelectedDriver.current = selectedDriver
  }, [selectedDriver])

  useEffect(() => {
    const interval = setInterval(() => {
      intervalGetOperators()
    }, INTERVAL)
    return () => clearInterval(interval)
  }, [])

  const generatePopupId = () => {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)

    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    let uuid = ''
    for (let i = 0; i < bytes.length; i++) {
      const hex = bytes[i].toString(16).padStart(2, '0')
      uuid += hex
      if (i === 3 || i === 5 || i === 7 || i === 9) {
        uuid += '-'
      }
    }

    return uuid
  }

  useEffect(() => {
    if (changeCount.current < 1 && drivers.length !== 0) {
      drivers.forEach(driver => {
        driver.trips.forEach(trip => {
          if (trip.hasIssue) {
            seenTripsRef.current.set(trip.id, getStatusText(trip))
          } else {
            seenTripsRef.current.set(trip.id, 'none')
          }
        })
      })
      changeCount.current += 1
      return
    }

    drivers.forEach(driver => {
      driver.trips.forEach(trip => {
        const previousHasIssue = seenTripsRef.current.get(trip.id)

        if (trip.hasIssue) {
          if (
            previousHasIssue === undefined ||
            previousHasIssue === 'none' ||
            previousHasIssue !== getStatusText(trip)
          ) {
            showNotification(
              'Alert',
              `${getStatusText(trip)} detected for ${trip.rider.firstName} ${trip.rider.lastName}.`,
              true
            )
          }
        }
        seenTripsRef.current.set(trip.id, getStatusText(trip))
      })
    })
  }, [drivers])

  const showNotification = (title: string, body: string, isIssue = false) => {
    const newNotification: Notification = {
      id: generatePopupId(),
      title,
      body,
      isIssue
    }
    setQueuedNotifications(prev => [...prev, newNotification])
  }

  const closeNotification = (id: string) => {
    setActiveNotifications(prev => prev.filter(n => n.id !== id))
  }

  useEffect(() => {
    if (activeNotifications.length >= 5 || queuedNotifications.length === 0) {
      return
    }

    const toAdd = Math.min(5 - activeNotifications.length, queuedNotifications.length)
    const nextNotifications = queuedNotifications.slice(0, toAdd)
    setActiveNotifications(prev => [...prev, ...nextNotifications])
    setQueuedNotifications(prev => prev.slice(toAdd))
  }, [activeNotifications, queuedNotifications])

  const getStatusText = (trip: IDriverManifestsTrip) => {
    if (trip.notificationMessage.toLowerCase().includes('vehicle')) {
      return 'Vehicle Issue'
    } else if (trip.notificationMessage.toLowerCase().includes('behind')) {
      return 'Late Trip'
    } else if (trip.lastStatus === TripStatuses.NO_SHOW_RIDER) {
      return 'No Show Trip'
    } else if (cancelledTripsStatusesDashboard.includes(trip.lastStatus)) {
      return 'Cancelled Trip'
    } else {
      return 'Issue'
    }
  }

  const checkSelectedDriver = () => {
    if (selectedDriver) {
      const updatedDriver = drivers.find(driver => driver.operatorId === selectedDriver.operatorId)
      if (updatedDriver) {
        setSelectedDriver(updatedDriver)
      }
    }
  }

  const checkSelectedTrip = () => {
    if (selectedTrip) {
      const updatedTrip = trips.find(trip => trip.id === selectedTrip.id)
      if (updatedTrip) {
        setSelectedTrip(updatedTrip)
      }
    }
  }

  const getOperators = async () => {
    const orgId = localStorage.getItem('currentOrganizationId')

    if (orgId) {
      const params: IDriverManifestsRetrieveParameters = {
        orgId,
        tripDate: moment().toISOString(),
        timezone: moment.tz.guess()
      }

      try {
        setLoading(true)
        const response: any = await tripService.getDriverManifests(params)
        const data = response.data
        setDrivers(data.drivers)
        setTotalDrivers(data.totalDrivers)
        setLoading(false)
        setIsUpdated(true)
        const body = 'Successfully retrieved driver manifests.'
        const title = 'Success'
        showNotification(title, body)
      } catch (e) {
        console.log('error: ', e)
        setLoading(false)
        setIsUpdated(false)
      }
    }
  }

  const intervalGetOperators = async () => {
    const orgId = localStorage.getItem('currentOrganizationId')
    const alert = tabRef.current === 'alerts' ? 'all' : tabRef.current
    if (orgId) {
      const params: IDriverManifestsRetrieveParameters = {
        orgId,
        tripDate: moment().toISOString(),
        alert: alert === 'all' ? undefined : alert,
        search: queryRef.current,
        timezone: moment.tz.guess()
      }
      try {
        const response: any = await tripService.getDriverManifests(params)
        const data: IDriverManifestsAlertReport = response.data

        if (tabRef.current === 'alerts') {
          const driversWithAlerts = data.drivers
            .map(driver => {
              const filteredTrips = driver.trips.filter(trip => trip.hasIssue)
              return {
                ...driver,
                trips: filteredTrips
              }
            })
            .filter(driver => driver.trips.length > 0)
          setDrivers(driversWithAlerts)
          setTotalDrivers(driversWithAlerts.length)
        } else {
          setDrivers(data.drivers)
          setTotalDrivers(data.totalDrivers)
        }
        setIsUpdated(true)
      } catch (e) {
        console.log('error: ', e)
        setIsUpdated(false)
      }
    }
  }

  const getTripSectionTitle = () => {
    if (selectedDriver) {
      return `Trips for ${selectedDriver.firstName} ${selectedDriver.lastName}`
    }
    return 'Select a driver to view trips'
  }

  const changeQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
    debouncedQuery(event.target.value)
  }

  const debouncedQuery = useCallback(
    debounce(1000, async (value: string) => {
      const orgId = localStorage.getItem('currentOrganizationId')
      const alert = tab === 'alerts' ? 'all' : tab
      if (orgId) {
        const params: IDriverManifestsRetrieveParameters = {
          orgId,
          tripDate: moment().toISOString(),
          alert: alert === 'all' ? undefined : alert,
          search: value ? value : undefined,
          timezone: moment.tz.guess()
        }
        try {
          setLoading(true)
          const response: any = await tripService.getDriverManifests(params)
          const data: IDriverManifestsAlertReport = response.data
          setSelectedDriver(null)
          setSelectedTrip(null)

          if (tab === 'alerts') {
            const driversWithAlerts = data.drivers
              .map(driver => {
                const filteredTrips = driver.trips.filter(trip => trip.hasIssue)
                return {
                  ...driver,
                  trips: filteredTrips
                }
              })
              .filter(driver => driver.trips.length > 0)
            setDrivers(driversWithAlerts)
            setTotalDrivers(driversWithAlerts.length)
          } else {
            setDrivers(data.drivers)
            setTotalDrivers(data.totalDrivers)
          }
          setLoading(false)
          setIsUpdated(true)
        } catch (e) {
          console.log('error: ', e)
          setLoading(false)
          setIsUpdated(false)
        }
      }
    }),
    [tab]
  )

  const changeTab = (value: 'all' | 'alerts' | 'behind' | 'no_show' | 'vehicle') => {
    setTab(value)
    filterByStatus(value)
  }

  const filterByStatus = async (value: 'all' | 'alerts' | 'behind' | 'no_show' | 'vehicle') => {
    const orgId = localStorage.getItem('currentOrganizationId')
    const alert = value === 'alerts' ? 'all' : value
    if (orgId) {
      const params: IDriverManifestsRetrieveParameters = {
        orgId,
        tripDate: moment().toISOString(),
        alert: alert === 'all' ? undefined : alert,
        search: query ? query : undefined,
        timezone: moment.tz.guess()
      }
      try {
        setLoading(true)
        const response: any = await tripService.getDriverManifests(params)
        const data: IDriverManifestsAlertReport = response.data
        setSelectedDriver(null)
        setSelectedTrip(null)

        if (value === 'alerts') {
          const driversWithAlerts = data.drivers
            .map(driver => {
              const filteredTrips = driver.trips.filter(trip => trip.hasIssue)
              return {
                ...driver,
                trips: filteredTrips
              }
            })
            .filter(driver => driver.trips.length > 0)
          setDrivers(driversWithAlerts)
          setTotalDrivers(driversWithAlerts.length)
        } else {
          setDrivers(data.drivers)
          setTotalDrivers(data.totalDrivers)
        }
        setLoading(false)
        setIsUpdated(true)
      } catch (e) {
        console.log('error: ', e)
        setLoading(false)
        setIsUpdated(false)
      }
    }
  }

  const getTotalAlertCount = () => {
    const allTrips: IDriverManifestsTrip[] = []

    drivers.map(driver => {
      allTrips.push(...driver.trips)
    })

    const tripsWithAlerts = allTrips.filter(trip => trip.hasIssue)

    setAlertCount(tripsWithAlerts.length)
  }

  const LoadingSpinner = ({ message = 'Retrieving Driver Manifests...' }) => (
    <div className='flex items-center justify-center h5 font-grey2 h100 flex-column'>
      <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
      <span className='font-grey2 h5 center pt2'>{message}</span>
    </div>
  )

  return (
    <div className='driver-manifests-page'>
      <div className='dm-top-section'>
        <div className='dm-search-container'>
          <Input
            disabled={loading}
            type='text'
            className='mobiscroll-input-white form dm-search-bar'
            placeholder='Search drivers, passengers, or locations...'
            theme='ios'
            themeVariant='light'
            inputStyle='box'
            startIcon='material-search'
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => changeQuery(event)}
          />
        </div>
        <div className='dm-tab-container'>
          <SegmentedGroup
            disabled={loading}
            value={tab}
            onChange={(e: any) => changeTab(e.target.value)}
            name='range'
            theme='ios'
            themeVariant='light'
            className='dm-tab-filter'
          >
            <Segmented value='all' theme='ios' themeVariant='light'>
              All
            </Segmented>
            <Segmented value='alerts' theme='ios' themeVariant='light'>
              Alerts
              <div className='dm-alert-tag-tab'>{alertCount}</div>
            </Segmented>
            <Segmented value='behind' theme='ios' themeVariant='light'>
              Behind
            </Segmented>
            <Segmented value='no_show' theme='ios' themeVariant='light'>
              No Shows
            </Segmented>
            <Segmented value='vehicle' theme='ios' themeVariant='light'>
              Vehicle
            </Segmented>
          </SegmentedGroup>
        </div>
      </div>
      <div className='dm-driver-container'>
        <div className='dm-section'>
          <div className='dm-section-inner'>
            <div className='dm-section-header'>Drivers ({totalDrivers})</div>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className={drivers.length > 0 ? 'dm-scrollable' : ''}>
                {drivers.map(driver => {
                  return (
                    <DriverManifestsDriverItem
                      drivers={drivers}
                      driver={driver}
                      key={driver.operatorId}
                      selectedDriver={selectedDriver}
                      setSelectedDriver={setSelectedDriver}
                      setSelectedTrip={setSelectedTrip}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div className='dm-section-trips'>
          <div className='dm-section-inner'>
            <div className='dm-section-header'>{getTripSectionTitle()}</div>
            {loading ? (
              <LoadingSpinner />
            ) : trips.length === 0 ? (
              <div className='dm-trip-section-empty-state'>Please select a driver from the list.</div>
            ) : (
              <div className={trips.length > 0 ? 'dm-scrollable' : ''}>
                {trips.map(trip => {
                  return (
                    <DriverManifestsTripItem
                      trip={trip}
                      selectedTrip={selectedTrip}
                      setSelectedTrip={setSelectedTrip}
                      key={trip.id}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <div className='dm-section-trips'>
          <div className='dm-section-inner'>
            {loading ? (
              <LoadingSpinner />
            ) : selectedTrip === null ? (
              <div className='dm-trip-section-empty-state'>Select a trip to view details.</div>
            ) : (
              <>
                <div className='dm-section-header'>Trip Details</div>
                <div className='dm-scrollable'>
                  <DriverManifestsTripDetailsPane selectedTrip={selectedTrip} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className='dm-notifications-container'>
        {activeNotifications.map(notification => (
          <DriverManifestsNotificationPopup
            key={notification.id}
            id={notification.id}
            title={notification.title}
            body={notification.body}
            onClose={closeNotification}
            isIssue={notification.isIssue}
          />
        ))}
      </div>
    </div>
  )
}

export default DriverManifestsPage



// WEBPACK FOOTER //
// ./src/components/driver-manifests/DriverManifestsPage.tsx